/**
 * athena-js
 *
 * database driver API gateway SDK — Athena client for the Athena HTTP API
 * built by XYLEX Group
 */

export type {
  AthenaAdminQueryExecutionMetadata,
  AthenaAdminQueryInput,
  AthenaAdminQueryResult,
  AthenaExpectedQueryShape,
  AthenaRawQueryDiagnosticsMode,
  AthenaRawQueryOperation,
} from "./admin/query.ts";
export {
  ATHENA_ADMIN_QUERY_EMPTY_SQL,
  ATHENA_ADMIN_QUERY_INVALID_SHAPE,
  ATHENA_ADMIN_QUERY_MULTI_STATEMENT,
  ATHENA_RAW_SQL_COMPAT_DEPRECATED,
  classifyRawSqlOperation,
  createAdminQuery,
  sqlLooksLikeMultipleStatements,
} from "./admin/query.ts";
export type {
  AthenaAdminEmailCreateRequest,
  AthenaAdminEmailDeleteRequest,
  AthenaAdminEmailEventTypeListResponse,
  AthenaAdminEmailEventTypeRecord,
  AthenaAdminEmailFailureCreateRequest,
  AthenaAdminEmailFailureDeleteRequest,
  AthenaAdminEmailFailureGetQuery,
  AthenaAdminEmailFailureGetResponse,
  AthenaAdminEmailFailureListQuery,
  AthenaAdminEmailFailureListResponse,
  AthenaAdminEmailFailureUpdateRequest,
  AthenaAdminEmailFailureUpdateResponse,
  AthenaAdminEmailGetQuery,
  AthenaAdminEmailGetResponse,
  AthenaAdminEmailListQuery,
  AthenaAdminEmailListResponse,
  AthenaAdminEmailTemplateCreateRequest,
  AthenaAdminEmailTemplateDeleteRequest,
  AthenaAdminEmailTemplateGetQuery,
  AthenaAdminEmailTemplateGetResponse,
  AthenaAdminEmailTemplateListQuery,
  AthenaAdminEmailTemplateListResponse,
  AthenaAdminEmailTemplateRecord,
  AthenaAdminEmailTemplateSendRequest,
  AthenaAdminEmailTemplateSendResponse,
  AthenaAdminEmailTemplateUpdateRequest,
  AthenaAdminEmailUpdateRequest,
  AthenaApiKeyRecord,
  AthenaAdminEmailUpdateResponse,
  AthenaAdminListUsersFilterOperator,
  AthenaAdminListUsersQuery,
  AthenaAdminListUsersSearchOperator,
  AthenaAuthAdminLimits,
  AthenaAuthAdminUserSessionRevokeBinding,
  AthenaAuthAfterHookPayload,
  AthenaAuthBaseURLConfig,
  AthenaAuthBeforeHookPayload,
  AthenaAuthBindings,
  AthenaAuthCallOptions,
  AthenaAuthCapabilitiesFeatures,
  AthenaAuthCapabilitiesResult,
  AthenaAuthCapabilitiesSource,
  AthenaAuthCapabilitiesStatus,
  AthenaAuthCapabilitiesStore,
  AthenaAuthClientConfig,
  AthenaAuthContext,
  AthenaAuthCookieRuntimeInput,
  AthenaAuthCredentials,
  AthenaAuthDatabaseFactory,
  AthenaAuthDomainEvent,
  AthenaAuthEmailChangeResponse,
  AthenaAuthEmailTemplateAttachment,
  AthenaAuthEmailTemplateBuilder,
  AthenaAuthEmailTemplateCreateFromDefinitionInput,
  AthenaAuthEmailTemplateDefinition,
  AthenaAuthEmailTemplateReactOverrides,
  AthenaAuthEmailTemplateUpdateFromDefinitionInput,
  AthenaAuthEmailTemplateVariableBinding,
  AthenaAuthEndpointPath,
  AthenaAuthErrorCode,
  AthenaAuthErrorDetails,
  AthenaAuthFilterOperator,
  AthenaAuthGenericInput,
  AthenaAuthGenericQueryInput,
  AthenaAuthGetUserResponse,
  AthenaAuthGuardFailure,
  AthenaAuthGuardReason,
  AthenaAuthGuardResult,
  AthenaAuthGuardSuccess,
  AthenaAuthHandlerResult,
  AthenaAuthHookActor,
  AthenaAuthHookContext,
  AthenaAuthHookErrorInput,
  AthenaAuthHooks,
  AthenaAuthHttpHandlers,
  AthenaAuthImplementedDomainEvent,
  AthenaAuthLinkedAccount,
  AthenaAuthMethod,
  AthenaAuthOrganization,
  AthenaAuthOrganizationBindings,
  AthenaAuthOrganizationInvitation,
  AthenaAuthOrganizationMember,
  AthenaAuthorizationRightDefinition,
  AthenaAuthorizationRoleDetail,
  AthenaAuthPlugin,
  AthenaAuthPluginContext,
  AthenaAuthPluginHandlerContext,
  AthenaAuthQueryPrimitive,
  AthenaAuthQueryValue,
  AthenaAuthReactEmailComponent,
  AthenaAuthReactEmailConfig,
  AthenaAuthReactEmailEventPhase,
  AthenaAuthReactEmailProps,
  AthenaAuthReactEmailRenderEvent,
  AthenaAuthReactEmailRenderInput,
  AthenaAuthReactEmailRenderOptions,
  AthenaAuthRequestContext,
  AthenaAuthRequestInput,
  AthenaAuthReservedDomainEvent,
  AthenaAuthResetPasswordBinding,
  AthenaAuthResult,
  AthenaAuthRevokeSessionRequest,
  AthenaAuthSearchOperator,
  AthenaAuthServer,
  AthenaAuthServerApi,
  AthenaAuthServerBindings,
  AthenaAuthServerConfig,
  AthenaAuthServerRuntimeOptions,
  AthenaAuthSession,
  AthenaAuthSessionResponse,
  AthenaAuthSessionRevokeBinding,
  AthenaAuthSignInResponse,
  AthenaAuthSignOutResponse,
  AthenaAuthSocialProviderConfig,
  AthenaAuthSocialProviderExtensions,
  AthenaAuthSocialRedirectResponse,
  AthenaAuthStatusResponse,
  AthenaAuthTrustedOrigins,
  AthenaAuthTrustedProviders,
  AthenaPasskeyRecord,
  AthenaAuthUser,
  AthenaChangeEmailRequest,
  AthenaChangePasswordRequest,
  AthenaDeleteUserCallbackRequest,
  AthenaDeleteUserRequest,
  AthenaDeleteUserResponse,
  AthenaEmailSignInRequest,
  AthenaEmailSignUpRequest,
  AthenaForgetPasswordRequest,
  AthenaLinkSocialRequest,
  AthenaOAuthAccountTokenRequest,
  AthenaOAuthTokenBundle,
  AthenaResetPasswordRequest,
  AthenaSendVerificationEmailRequest,
  AthenaSocialSignInRequest,
  AthenaUnlinkAccountRequest,
  AthenaUpdateUserRequest,
  AthenaUsernameSignInRequest,
  AthenaVerifyEmailRequest,
  AuthBindings,
  AuthOAuthProvider,
  AuthorizationCapabilities,
  AuthorizationSnapshot,
  AuthSocialProvider,
  AuthSocialProviderBuiltin,
  RoleDescriptor,
} from "./auth/index.ts";
export {
  ATHENA_AUTH_ADMIN_LIMITS,
  ATHENA_AUTH_BASE_ERROR_CODES,
  ATHENA_AUTH_DOMAIN_EVENTS,
  ATHENA_AUTH_EMBEDDED_CAPABILITY_SNAPSHOT,
  ATHENA_AUTH_IMPLEMENTED_DOMAIN_EVENTS,
  ATHENA_AUTH_MAX_ADMIN_JSON_BYTES,
  ATHENA_AUTH_MAX_ADMIN_JSON_DEPTH,
  ATHENA_AUTH_MAX_TEMPLATE_VARIABLE_LENGTH,
  ATHENA_AUTH_MAX_TEMPLATE_VARIABLES,
  ATHENA_AUTH_RESERVED_DOMAIN_EVENTS,
  ATHENA_AUTHORIZATION_SNAPSHOT_INVALID,
  AUTH_EMAIL_EVENT_CATALOG,
  AuthorizationSnapshotInvalidError,
  athenaAuth,
  authEmailEvents,
  capabilitiesFromRights,
  createAthenaAuthCapabilitiesStore,
  createAuthReactEmailInput,
  createEmbeddedCapabilitySnapshot,
  defineAthenaAuthConfig,
  defineAthenaAuthHooks,
  defineAuthEmailTemplate,
  flattenAuthEmailEvents,
  isCapabilityEnabled,
  isPasskeyOnboardingEnabled,
  isSocialCapabilityEnabled,
  parseAuthorizationSnapshot,
  renderAthenaReactEmail,
  renderAuthEmailFragment,
  resolveSocialProvidersForUi,
} from "./auth/index.ts";
export type {
  AthenaErrorInput,
  AthenaOperationContext,
  IntCoercionOptions,
  NormalizedAthenaError,
  RequireAffectedOptions,
  RetryBackoffStrategy,
  RetryConfig,
  UnwrapOneOptions,
  UnwrapOptions,
} from "./auxiliaries.js";
export {
  AthenaError,
  AthenaErrorCategory,
  AthenaErrorCode,
  AthenaErrorKind,
  assertInt,
  coerceInt,
  isOk,
  normalizeAthenaError,
  parseBooleanFlag,
  requireAffected,
  requireSuccess,
  unwrap,
  unwrapOne,
  unwrapRows,
  withRetry,
} from "./auxiliaries.js";
export type {
  AthenaBillingCallOptions,
  AthenaBillingClientConfig,
  AthenaBillingEnvelope,
  AthenaBillingHttpMethod,
  AthenaBillingJson,
  AthenaBillingModule,
  BillingConnectionRefInput,
  BillingCreateConnectionInput,
  BillingEnsureCustomerInput,
  BillingListQuery,
  BillingProvisionSinksInput,
  BillingReconcileInput,
  BillingUpdateConnectionInput,
  BillingUpdateCustomerInput,
} from "./billing/index.ts";
export {
  AthenaBillingError,
  billingSdkManifest,
  createBillingModule,
} from "./billing/index.ts";
export {
  AthenaChatError,
  chatSdkManifest,
  unwrapChatMessage,
  unwrapChatRoom,
} from "./chat/module.ts";
export type * from "./chat/types.js";
export type {
  AthenaClientAdminModule,
  AthenaClientSystemModule,
  AthenaFromOptions,
  AthenaQueryTraceCallsite,
  AthenaQueryTraceEvent,
  AthenaQueryTraceOptions,
  AthenaRequestMethod,
  AthenaRequestOptions,
  AthenaRequestQueryValueMap,
  AthenaRequestResponse,
  AthenaRequestService,
  AthenaResult,
  AthenaResultError,
  RpcOrderOptions,
  RpcQueryBuilder,
  TableQueryBuilder,
} from "./client.js";
export type {
  AthenaExecutionMode,
  AthenaExecutionModeInput,
  AthenaExecutionPrefer,
  AthenaExecutionPreferInput,
  AthenaResolvedExecutionMode,
} from "./cloudflare/execution-mode.ts";
export { resolveAthenaExecutionMode } from "./cloudflare/execution-mode.ts";
export type { AthenaClientCapabilities } from "./cloudflare/types.ts";
export type {
  AthenaCompatibilityReport,
  AthenaCompatibilityWarning,
} from "./compatibility/report.ts";
export {
  buildCompatibilityReportFromHealth,
  buildUndiscoveredCompatibilityReport,
} from "./compatibility/report.ts";
export type {
  AnyPageRequest,
  AthenaErrorBody,
  AthenaErrorResponse,
  AthenaTransportErrorCode as AthenaTransportErrorCodeName,
  CursorPageRequest,
  JsonObject,
  JsonPrimitive,
  JsonValue,
  LimitPolicy,
  OffsetPage,
  OffsetPageRequest,
  Page,
  PaginationLimitPolicyName,
  SequencePage,
  SequencePageRequest,
} from "./contracts/v1/index.ts";
export {
  AthenaTransportErrorCode,
  PaginationLimitPolicy,
} from "./contracts/v1/index.ts";
export type {
  AthenaDbModule,
  AthenaTransactionClient,
} from "./db/module.js";
export type {
  AthenaExecutableOutput,
  AthenaTransactionBackend,
  AthenaTransactionCapabilities,
  AthenaTransactionErrorCode,
  AthenaTransactionIsolationLevel,
  AthenaTransactionOptions,
  AthenaTransactionResults,
} from "./db/transaction/index.ts";
export {
  AthenaTransactionError,
  isAthenaTransactionError,
} from "./db/transaction/index.ts";
export type {
  AthenaEmailAttachment,
  AthenaEmailAttachmentFailureMode,
  AthenaEmailAttachmentPolicy,
  AthenaEmailConfig,
  AthenaEmailDefaults,
  AthenaEmailDeliveryKind,
  AthenaEmailDeliveryPort,
  AthenaEmailDeliveryResult,
  AthenaEmailDiagnostics,
  AthenaEmailMessage,
  AthenaEmailModule,
  AthenaEmailProvider,
  AthenaEmailProviderCapabilities,
  AthenaEmailProviderRuntime,
  AthenaResolvedEmailMessage,
} from "./email/index.ts";
export {
  ATHENA_EMAIL_DELIVERY_FAILED,
  ATHENA_EMAIL_MESSAGE_INVALID,
  ATHENA_EMAIL_PROVIDER_INVALID,
  ATHENA_EMAIL_PROVIDER_NOT_CONFIGURED,
  ATHENA_EMAIL_PROVIDER_UNSUPPORTED_RUNTIME,
  AthenaEmailError,
  assertAthenaEmailProviderRuntime,
  createEmailDeliveryPort,
  defineAthenaEmailProvider,
  httpEmailProvider,
  isAthenaEmailError,
  isAthenaEmailProvider,
  resend,
} from "./email/index.ts";
export {
  consoleEmailProvider,
  consoleEmailProvider as createConsoleEmailProvider,
} from "./email/providers/console.ts";
export { verifyAthenaGatewayUrl } from "./gateway/client.ts";
export { AthenaGatewayError, isAthenaGatewayError } from "./gateway/errors.ts";
export type {
  AthenaRouteClassification,
  AthenaRouteDescriptor,
  AthenaRouteStatus,
} from "./gateway/routes.ts";
export {
  ATHENA_GATEWAY_ROUTES,
  ATHENA_ROUTE_MANIFEST,
  getAthenaRouteDescriptor,
  isDeprecatedAthenaRoute,
} from "./gateway/routes.ts";
export type {
  AthenaConditionCastType,
  AthenaGatewayCallOptions,
  AthenaGatewayConnectionOptions,
  AthenaGatewayConnectionResult,
  AthenaGatewayErrorCode,
  AthenaGatewayErrorDetails,
  AthenaJsonArray,
  AthenaJsonObject,
  AthenaJsonPrimitive,
  AthenaJsonValue,
  AthenaRpcCallOptions,
  AthenaRpcFilter,
  AthenaRpcFilterOperator,
  AthenaRpcOrder,
  AthenaRpcPayload,
  BackendConfig,
  BackendType,
} from "./gateway/types.js";
export { Backend } from "./gateway/types.js";
export { normalizeAthenaGatewayBaseUrl } from "./gateway/url.ts";
export type {
  AthenaConfig,
  AthenaGeneratorConfig,
  AthenaPolicyProjectConfig,
  AthenaToolingEntrypoints,
  EnsureGeneratorConfigFileOptions,
  EnsureGeneratorConfigFileResult,
  GeneratedArtifact,
  GeneratedArtifacts,
  GeneratedManifest,
  GeneratorArtifactKind,
  GeneratorConfigEnsureSummary,
  GeneratorConfigFileAction,
  GeneratorConfigProviderMode,
  GeneratorDatabaseAuthorityMode,
  GeneratorDatabaseAuthoritySource,
  GeneratorEnvBooleanOptions,
  GeneratorEnvJsonOptions,
  GeneratorEnvListOptions,
  GeneratorEnvOneOfOptions,
  GeneratorEnvStringOptions,
  GeneratorExperimentalFlags,
  GeneratorFeatureFlags,
  GeneratorFilterConfig,
  GeneratorInternalConfig,
  GeneratorNamingConfig,
  GeneratorOutputConfig,
  GeneratorOutputFormat,
  GeneratorOutputPreset,
  GeneratorOutputTargets,
  GeneratorProviderConfig,
  GeneratorSchemaProvenance,
  GeneratorSchemaSelection,
  GeneratorTableSelection,
  LoadAthenaConfigOptions,
  LoadedAthenaConfig,
  LoadedGeneratorConfig,
  LoadGeneratorConfigOptions,
  NamingStyle,
  NormalizedAthenaGeneratorConfig,
  NormalizedGeneratorFilterConfig,
  NormalizedGeneratorOutputConfig,
  RenderGeneratedFileHeaderOptions,
  RenderObjectPropertyOptions,
  ResolvedGeneratorDatabaseAuthority,
  ResolveGeneratorDatabaseAuthorityOptions,
  RunGeneratorOptions,
  RunGeneratorResult,
  SkippedGeneratedArtifact,
  SkippedGeneratedArtifactReason,
} from "./generator/index.ts";
export {
  ATHENA_GENERATED_FILE_PREFIX,
  ATHENA_GENERATED_ROOT,
  applyGeneratorProjectEnv,
  DEFAULT_POSTGRES_SCHEMAS,
  defineAthenaConfig,
  defineGeneratorConfig,
  detectAuthorityMode,
  detectGeneratorProviderMode,
  discoverPostgresSchemas,
  ensureGeneratorConfigFile,
  filterIntrospectionSnapshot,
  findGeneratorConfigPath,
  formatSchemaFallbackMessages,
  GENERATED_FILE_BANNER,
  GENERATED_MANIFEST_REL,
  generateArtifactsFromSnapshot,
  generatorEnv,
  isAthenaGeneratedSource,
  loadAthenaConfig,
  loadGeneratorConfig,
  mergeSchemaSelections,
  normalizeDiscoveredSchemas,
  normalizeGeneratorConfig,
  normalizeSchemaSelection,
  normalizeTableSelection,
  patchSchemasInConfigSource,
  renderGeneratedFileHeader,
  renderGeneratorConfigFile,
  renderObjectKey,
  renderObjectLiteral,
  renderObjectProperty,
  resolveGeneratorDatabaseAuthority,
  resolveGeneratorProvider,
  resolvePostgresColumnType,
  resolveProviderSchemas,
  runSchemaGenerator,
  schemasEqual,
  stripGeneratedFileHeader,
  withGeneratedFileBanner,
} from "./generator/index.ts";
export {
  clampPaginationLimit,
  mapAthenaErrorCodeToTransportCode,
  mapChatMessagePageWireToSequencePage,
  mapLimitPlusOneToPage,
  mapNormalizedAthenaErrorToErrorResponse,
  mapOffsetWindowToOffsetPage,
} from "./mappers/index.ts";
export {
  athenaNotificationCatalogDemo,
  NOTIFICATION_CATALOG,
} from "./notifications/catalog.ts";
export type {
  AthenaNotificationsConfig,
  NotificationCatalogEntry,
} from "./notifications/types.ts";
export type {
  ActiveOrganizationSessionLike,
  EnsureActiveOrganizationOptions,
  EnsureActiveOrganizationResult,
  OrganizationLike,
} from "./organization/index.ts";
export { ensureActiveOrganization } from "./organization/index.ts";
export {
  canonicalizeAthenaValue,
  hashAthenaValue,
} from "./query/canonicalize.ts";
export type {
  AthenaCacheContextDescriptor,
  AthenaCacheScope,
  AthenaExecutable,
  AthenaExecuteOptions,
  AthenaFieldDependency,
  AthenaFilterDescriptor,
  AthenaModelDependency,
  AthenaOrderDescriptor,
  AthenaPagination,
  AthenaPredicateNode,
  AthenaProjectionDescriptor,
  AthenaProjectionKind,
  AthenaQueryDependencyDescriptor,
  AthenaQueryDescriptor,
  AthenaQueryDescriptorCompileInput,
  AthenaQueryFieldDependencyKind,
  AthenaQueryOperation,
  AthenaQueryTarget,
  AthenaRangeDescriptor,
  AthenaRelationDependency,
  AthenaRelationDescriptor,
  AthenaSelectionNode,
} from "./query/descriptor.ts";
export {
  ATHENA_EXECUTABLE,
  buildAthenaModelScopeKey,
  buildAthenaQueryKey,
  compileAthenaQueryDescriptor,
  createCapturedAthenaExecutable,
  isAthenaExecutable,
  resolveAthenaQueryTarget,
} from "./query/descriptor.ts";
export type { AthenaQueryExplanation } from "./query/explain.ts";
export { explainAthenaQuery } from "./query/explain.ts";
export type {
  AthenaEntityContextIdentity,
  AthenaEntityKey,
  AthenaModelIdentity,
  AthenaPrimaryKey,
} from "./query/model-identity.ts";
export {
  athenaEntityKeyToken,
  createAthenaEntityKey,
  entityKeyFromSinglePrimary,
  modelIdentity,
} from "./query/model-identity.ts";
export type {
  AthenaModelView,
  AthenaModelViewDefinition,
  AthenaModelViewField,
} from "./query/model-view.ts";
export { defineModelView } from "./query/model-view.ts";
export * from "./query/read-query-public.ts";
export {
  descriptorFromReadQueryDefinition,
  readQueryDefinitionFromDescriptor,
} from "./query/read-query-descriptor.ts";
export type {
  AthenaFindManyOptions,
  AthenaFindManyResult,
  AthenaOrderBy,
  AthenaRelationSelectNode,
  AthenaSelectShape,
  AthenaValidatedSelectShape,
  AthenaWhere,
  AthenaWhereBooleanOperand,
  AthenaWhereOperatorInput,
} from "./query-ast.ts";
export type {
  AthenaDeleteDebugAst,
  AthenaFindManyDebugAst,
  AthenaInsertDebugAst,
  AthenaQueryDebugAst,
  AthenaRawQueryDebugAst,
  AthenaRpcBuilderStateAst,
  AthenaRpcDebugAst,
  AthenaSelectDebugAst,
  AthenaSelectDebugTransport,
  AthenaTableBuilderStateAst,
  AthenaUpdateDebugAst,
  AthenaUpsertDebugAst,
} from "./query-debug-ast.ts";
export { getAthenaDebugAst } from "./query-debug-ast.ts";
export type {
  AthenaNormalizedHealth,
  AthenaReleaseChannel,
  AthenaReleaseIdentity,
} from "./release/identity.ts";
export {
  normalizeAthenaHealthPayload,
  normalizeAthenaReleaseIdentity,
} from "./release/identity.ts";
export type {
  AthenaDataLifecycleConfig,
  AthenaDataLifecycleEvent,
  AthenaDataLifecycleHook,
  AthenaDataLifecycleHooks,
} from "./runtime/data/lifecycle/index.ts";
export type { AthenaContractIssue } from "./runtime/index.ts";
export {
  AthenaContractParseError,
  athenaErrorBodySchema,
  athenaErrorResponseSchema,
  athenaTransportErrorCodeSchema,
  cursorPageRequestSchema,
  jsonObjectSchema,
  jsonPrimitiveSchema,
  jsonValueSchema,
  offsetPageRequestSchema,
  offsetPageSchema,
  pageSchema,
  parseContractOrThrow,
  safeParseContract,
  sequencePageRequestSchema,
  sequencePageSchema,
} from "./runtime/index.ts";
export type {
  AnyColumnBuilder,
  AthenaClientModelForTableName,
  AthenaClientModelsInput,
  AthenaClientTableName,
  AthenaColumnBuilder,
  AthenaModelTarget,
  AthenaSchemaSnapshot,
  AthenaTableDef,
  AthenaTableSchemaBundle,
  ColumnRuntimeConfig,
  DatabaseDef,
  DecimalColumnOptions,
  DiffSchemasInput,
  DiffSchemasOptions,
  FormValuesFromColumns,
  FormValuesOf,
  InsertFromColumns,
  InsertOf,
  IntrospectionColumn,
  IntrospectionInspectOptions,
  IntrospectionRelation,
  IntrospectionSchema,
  IntrospectionSnapshot,
  IntrospectionTable,
  IntrospectionTypeKind,
  ModelAt,
  ModelColumnKind,
  ModelColumnMetadata,
  ModelDef,
  ModelFormAdapter,
  ModelFormDefaults,
  ModelFormNullishMode,
  ModelFormValues,
  ModelMetadata,
  ModelRelationKind,
  ModelRelationMetadata,
  ModelSqlDialect,
  ModelSqlFile,
  ModelSqlInput,
  ModelSqlOptions,
  ModelsToSqlFilesOptions,
  PostgresIntrospectionProviderOptions,
  RegistryDef,
  RowFromColumns,
  RowOf,
  SchemaColumn,
  SchemaColumnType,
  SchemaDef,
  SchemaDiff,
  SchemaDiffErrorCode,
  SchemaDiffOperation,
  SchemaDiffOperationKind,
  SchemaDiffSummary,
  SchemaForeignKey,
  SchemaIndex,
  SchemaIntrospectionProvider,
  SchemaNamespace,
  SchemaPrimaryKey,
  SchemaReferentialAction,
  SchemaSnapshotFromIntrospectionOptions,
  SchemaSnapshotFromModelsOptions,
  SchemaTable,
  SchemaTableIdentity,
  SchemaUniqueConstraint,
  TenantContext,
  TenantContextValue,
  TenantKeyMap,
  ToModelFormDefaultsOptions,
  ToModelPayloadOptions,
  UpdateFromColumns,
  UpdateOf,
} from "./schema/index.ts";
export {
  ATHENA_INTERNAL_SCHEMAS,
  ATHENA_SCHEMA_SNAPSHOT_VERSION,
  boolean,
  columnsEqual,
  columnTypesEqual,
  createModelFormAdapter,
  createPostgresIntrospectionProvider,
  decimal,
  defineDatabase,
  defineModel,
  defineRegistry,
  defineSchema,
  diffSchemas,
  emptySchemaSnapshot,
  enumeration,
  isSchemaDiffEmpty,
  json,
  modelsToSql,
  modelsToSqlFiles,
  normalizeDefaultExpression,
  normalizeReferentialAction,
  normalizeSchemaColumnType,
  normalizeSchemaSnapshot,
  number,
  numeric,
  parseSchemaTypeString,
  primaryKeysEqual,
  SchemaDiffError,
  schemaSnapshotFromIntrospection,
  schemaSnapshotFromModels,
  sqlD1,
  sqlPostgres,
  sqlSqlite,
  string,
  summarizeSchemaDiffOperations,
  table,
  tableIdentityKey,
  toModelFormDefaults,
  toModelPayload,
  validateSchemaSnapshot,
} from "./schema/index.ts";
export type { WriteModelSqlFilesOptions } from "./schema/model-sql-write.ts";
export { writeModelSqlFiles } from "./schema/model-sql-write.ts";
export { identifier } from "./sql-identifiers.ts";
export type {
  AthenaStorageEnv,
  AthenaStorageFileConfig,
  AthenaStorageFileDeleteInput,
  AthenaStorageFileDownloadInput,
  AthenaStorageFileListInput,
  AthenaStorageFileModule,
  AthenaStorageFileUploadInput,
  AthenaStorageFileUploadResult,
  AthenaStoragePathContext,
  AthenaStoragePrefixPath,
  AthenaStorageTemplateValue,
  AthenaStorageTemplateVars,
  AthenaStorageUploadConstraints,
  AthenaStorageUploadedFile,
  AthenaStorageUploadProgress,
  AthenaStorageUploadProgressHandler,
  AthenaStorageUploadSource,
} from "./storage/file.js";
export {
  createStorageFileModule,
  DEFAULT_MULTIPART_PART_SIZE_BYTES,
  DEFAULT_MULTIPART_THRESHOLD_BYTES,
  resolveStoragePath,
} from "./storage/file.js";
export type * from "./storage/module.js";
export type {
  AthenaEnvelope,
  AthenaStorageBinaryCallOptions,
  AthenaStorageCallOptions,
  AthenaStorageClientConfig,
  AthenaStorageErrorDetails,
  AthenaStorageErrorHandler,
  AthenaStorageErrorInput,
  AthenaStorageModule,
  CreateStorageCatalogRequest,
  CreateStorageUploadUrlRequest,
  CreateStorageUploadUrlsRequest,
  DeleteStorageFolderRequest,
  GetStorageFileUrlQuery,
  ListStorageFilesRequest,
  ManagedFileRecord,
  MoveStorageFolderRequest,
  PresignedFileUrlResponse,
  S3CatalogItem,
  S3CredentialListItem,
  SetStorageFileVisibilityRequest,
  StorageBatchUploadUrlResponse,
  StorageFileAccessPurpose,
  StorageFileMutationResponse,
  StorageFolderMutationResponse,
  StorageListFilesResponse,
  StorageUploadUrlResponse,
  UpdateStorageCatalogRequest,
  UpdateStorageFileRequest,
} from "./storage/module.js";
export {
  AthenaStorageError,
  AthenaStorageErrorCode,
  createAthenaStorageError,
  requireStorageManifestRoute,
  storageLiveHttpRoutes,
  storageSdkManifest,
} from "./storage/module.ts";
export type {
  AthenaTableCatalogColumn,
  AthenaTableCatalogQueryClient,
  AthenaTableCatalogRelation,
  AthenaTableCatalogResponse,
  AthenaTableCatalogTable,
  AthenaTableSchemaConfig,
  AthenaTableSchemaHandlerOptions,
  AthenaTableShowcaseConfig,
  FetchAthenaTableCatalogOptions,
  TableCatalogColumn,
  TableCatalogRelation,
  TableCatalogResponse,
  TableCatalogTable,
} from "./tables/index.ts";
// Table schema catalog + App Router handlers (`/api/tables/schema`)
export {
  ATHENA_TABLE_SCHEMA_ROUTE,
  buildAthenaTableCatalogQueries,
  createAthenaTableSchemaHandlers,
  fetchAthenaTableCatalog,
  fetchTableCatalog,
  handleAthenaTableSchemaPost,
  hasAthenaTableSchemaCredentials,
  isAthenaTableSchemaConfig,
  parseAthenaTableSchemaScope,
} from "./tables/index.ts";
export type {
  AthenaAuthConfig,
  AthenaBillingConfig,
  AthenaChatConfig,
  AthenaChatMode,
  AthenaClient,
  AthenaClientConfig,
  AthenaClientConfigWithR2,
  AthenaClientRuntimeConfig,
  AthenaClientServicesConfig,
  AthenaClientWithR2Storage,
  AthenaConfigurationErrorCode,
  AthenaDbConfig,
  AthenaRequestContext,
  AthenaRequestContextProvider,
  AthenaService,
  AthenaStorageConfig,
} from "./v3-client.ts";
// Athena client
export { AthenaConfigurationError, createClient } from "./v3-client.ts";
